import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarRange,
  Car,
  Users,
  DollarSign,
  MapPin,
  Settings,
  Wrench,
  Table2,
  Bell,
  MoreHorizontal,
  Pencil,
  Check,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useRoleAccess } from "@/hooks/useRoleAccess";

type TabDef = { label: string; icon: React.ElementType; path: string };

const ALL_TABS: TabDef[] = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
  { label: "Bookings", icon: CalendarRange, path: "/bookings" },
  { label: "Fleet", icon: Car, path: "/fleet" },
  { label: "Customers", icon: Users, path: "/customers" },
  { label: "Financials", icon: DollarSign, path: "/financials" },
  { label: "Maintenance", icon: Wrench, path: "/maintenance" },
  { label: "Availability", icon: Table2, path: "/availability-table" },
  { label: "Alerts", icon: Bell, path: "/alerts" },
  { label: "Map", icon: MapPin, path: "/map" },
  { label: "Settings", icon: Settings, path: "/settings" },
];

const DEFAULT_PRIMARY = ["/dashboard", "/bookings", "/fleet", "/customers"];
const OWNER_ONLY_PATHS = ["/financials", "/map"];
const MAX_PRIMARY = 4;

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { isOwner } = useRoleAccess();

  const storageKey = `easy_rental_primary_tabs_${profile?.id || "guest"}`;

  const [primaryPaths, setPrimaryPaths] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : DEFAULT_PRIMARY;
    } catch {
      return DEFAULT_PRIMARY;
    }
  });

  const savePrimaryPaths = (paths: string[]) => {
    setPrimaryPaths(paths);
    try {
      localStorage.setItem(storageKey, JSON.stringify(paths));
    } catch {
      return;
    }
  };

  const availableTabs = ALL_TABS.filter((t) => !OWNER_ONLY_PATHS.includes(t.path) || isOwner);

  const primaryTabs = primaryPaths
    .map((path) => availableTabs.find((t) => t.path === path))
    .filter(Boolean)
    .slice(0, MAX_PRIMARY) as TabDef[];

  const secondaryTabs = availableTabs.filter((t) => !primaryPaths.includes(t.path));

  const isSecondaryActive = secondaryTabs.some((t) => location.pathname === t.path);

  const [draftPaths, setDraftPaths] = useState<string[]>(primaryPaths);

  const openEdit = () => {
    setDraftPaths(primaryPaths);
    setEditOpen(true);
  };

  const toggleDraft = (path: string) => {
    setDraftPaths((prev) => {
      if (prev.includes(path)) return prev.filter((p) => p !== path);
      if (prev.length >= MAX_PRIMARY) return prev;
      return [...prev, path];
    });
  };

  const saveEdit = () => {
    savePrimaryPaths(draftPaths);
    setEditOpen(false);
    setSheetOpen(false);
  };

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-30 border-t border-sidebar-border bg-sidebar safe-bottom">
        <div className="flex">
          {primaryTabs.map((tab) => {
            const active = location.pathname === tab.path;
            return (
              <button
                key={tab.path}
                data-tour={`nav-${tab.path}`}
                onClick={() => navigate(tab.path)}
                className={cn(
                  "flex flex-1 flex-col items-center gap-0.5 px-2 py-2 text-[10px] font-medium transition-colors",
                  active ? "text-sidebar-primary" : "text-sidebar-foreground/60"
                )}
              >
                <tab.icon className={cn("h-5 w-5", active && "stroke-[2.5px]")} />
                <span>{tab.label}</span>
              </button>
            );
          })}

          <button
            onClick={() => setSheetOpen(true)}
            className={cn(
              "flex flex-1 flex-col items-center gap-0.5 px-2 py-2 text-[10px] font-medium transition-colors",
              isSecondaryActive ? "text-sidebar-primary" : "text-sidebar-foreground/60"
            )}
          >
            <MoreHorizontal className={cn("h-5 w-5", isSecondaryActive && "stroke-[2.5px]")} />
            <span>More</span>
          </button>
        </div>
      </nav>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl border-sidebar-border bg-sidebar px-4 pb-8 pt-4">
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-sidebar-foreground/20" />
          <div className="mb-3 flex items-center justify-between">
            <SheetTitle className="text-xs font-medium uppercase tracking-wider text-sidebar-foreground/50">More</SheetTitle>
            <button
              onClick={openEdit}
              className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-sidebar-foreground/50 transition-colors hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
            >
              <Pencil className="h-3 w-3" /> Edit Tabs
            </button>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {secondaryTabs.map((tab) => {
              const active = location.pathname === tab.path;
              return (
                <button
                  key={tab.path}
                  onClick={() => { setSheetOpen(false); navigate(tab.path); }}
                  className={cn(
                    "flex flex-col items-center gap-1 rounded-xl px-2 py-3 text-[11px] font-medium transition-colors",
                    active
                      ? "bg-sidebar-accent text-sidebar-primary"
                      : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                  )}
                >
                  <tab.icon className={cn("h-5 w-5", active && "stroke-[2.5px]")} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

        </SheetContent>
      </Sheet>

      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl border-sidebar-border bg-sidebar px-4 pb-8 pt-4">
          <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-sidebar-foreground/20" />
          <SheetHeader className="mb-4 text-left">
            <SheetTitle className="text-base font-display">Customize Tabs</SheetTitle>
            <SheetDescription className="text-xs">
              Select up to {MAX_PRIMARY} tabs for quick access - {draftPaths.length}/{MAX_PRIMARY} selected.
            </SheetDescription>
          </SheetHeader>
          <div className="mb-6 grid grid-cols-4 gap-2">
            {availableTabs.map((tab) => {
              const selected = draftPaths.includes(tab.path);
              const disabled = !selected && draftPaths.length >= MAX_PRIMARY;
              return (
                <button
                  key={tab.path}
                  onClick={() => !disabled && toggleDraft(tab.path)}
                  className={cn(
                    "relative flex flex-col items-center gap-1 rounded-xl px-2 py-3 text-[11px] font-medium transition-colors",
                    selected
                      ? "bg-sidebar-primary/15 text-sidebar-primary ring-1 ring-sidebar-primary/40"
                      : disabled
                        ? "cursor-not-allowed text-sidebar-foreground/40 opacity-30"
                        : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                  )}
                >
                  {selected && (
                    <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-sidebar-primary">
                      <Check className="h-2.5 w-2.5 stroke-[3px] text-sidebar-primary-foreground" />
                    </span>
                  )}
                  <tab.icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
          <Button
            className="w-full"
            onClick={saveEdit}
            disabled={draftPaths.length === 0}
          >
            Done
          </Button>
        </SheetContent>
      </Sheet>
    </>
  );
}
